import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import logger from '@utils/logger';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import { CONFIG, ROUTES, SYSTEM_ADMIN_TOKEN_STORAGE_KEY } from '@/constants';
import { notifyNotificationBadgeShouldRefresh } from '@utils/notificationBadgeBus';

// Estender o tipo para incluir a propriedade _retry
interface ExtendedInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: CONFIG.API_TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Rotas públicas que não devem incluir token de autenticação
const publicRoutes = [
  '/csrf-token',
  '/public/stats',
  '/public/tenants',
  '/public/demands',
  '/public/clients/',
  '/public/config/',
  '/auth/login',
  '/auth/signup',
  '/auth/verify-2fa',
  '/auth/check-cnpj',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/pricing/config', // Endpoint público para calculadora
  '/terms/required', // Endpoint público para termos obrigatórios
  '/terms/active', // Endpoint público para termos ativos
  '/resources/videos/public', // Vídeos públicos da landing
  '/resources/videos/public/stream/', // Stream de vídeo público (proxy)
  '/public/trial-period-default', // Dias de trial padrão (landing)
  '/public/support-contact', // Contato institucional (sem auth)
];

// CSRF token - capturado das respostas e enviado em requisições mutantes
let csrfToken: string | null = null;

// Verificar se uma rota é pública
const isPublicRoute = (url: string | undefined): boolean => !!url && publicRoutes.some(route => url.includes(route));

// Rotas que não devem disparar fluxo de refresh automático em caso de 401
const noRefreshRoutes = [
  '/auth/verify',
  '/config/all',
  '/system-admin/',
  '/impersonation/status',
] as const;

const isNoRefreshRoute = (url: string | undefined): boolean => !!url && noRefreshRoutes.some(route => url.includes(route));

// Interceptor de requests: CSRF + Bearer system admin (pós-2FA cross-origin)
api.interceptors.request.use(
  async (config) => {
    // Achado real (F2 do plano de consolidação, 06/08/2026): antes, o Bearer token de system
    // admin só era anexado em URLs contendo '/system-admin/' ou '/impersonation/'. A sessão de
    // system admin pós-2FA NUNCA ganha o cookie httpOnly normal (só existe esse token em
    // sessionStorage) — então qualquer chamada fora desses dois prefixos (ex.: /auth/verify,
    // /config/all, chamado em toda navegação; /billing/config, usado pela própria tela de
    // Configuração Fiscal NFSe que só system admin acessa) sempre voltava 401/403, e o handler
    // global de 401 tratava isso como sessão inválida e redirecionava para a landing page — o
    // botão "Configuração fiscal NFSe" na tela de seleção de system admin literalmente não
    // funcionava. O backend (middleware/auth.ts) já aceita Bearer token em qualquer rota
    // ("mobile ou fallback web pós-2FA system admin"), então a correção é anexar o header sempre
    // que o token existir em sessionStorage, sem restringir por prefixo de URL — enquanto esse
    // token existir, a sessão está mesmo em modo system admin (removido em
    // handleStartImpersonation ao trocar para a sessão do usuário impersonado).
    if (typeof window !== 'undefined') {
      const saToken = sessionStorage.getItem(SYSTEM_ADMIN_TOKEN_STORAGE_KEY);
      if (saToken && config.headers) {
        config.headers.Authorization = `Bearer ${saToken}`;
      }
    }

    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      if (!csrfToken) {
        try {
          await api.get('/csrf-token', { timeout: 5000 });
        } catch {
          // Ignorar - tentaremos com token vazio (backend rejeitará se necessário)
        }
      }
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error: AxiosError) => {
    logger.error('Erro na requisição:', error.message);
    return Promise.reject(error);
  }
);

// Captura X-CSRF-Token de qualquer resposta (sucesso ou erro)
const captureCsrfToken = (headers: Record<string, string> | undefined): void => {
  if (!headers) return;
  const token = headers['x-csrf-token'] || headers['X-CSRF-Token'];
  if (typeof token === 'string' && token) {
    csrfToken = token;
  }
};

// Rotas cuja mutação pode mover algo pra dentro/fora de uma contagem do sino de notificações
// (openCount/inProgressCount/overdueCount/pendingSuggestionsCount/patrimonyDivergenceCount — ver
// NotificationBadgeService no backend). Checado só em respostas de sucesso de método mutante
// (isNotificationBadgeMutation abaixo) — bug real, relato do usuário: mover uma demanda pra fora
// das condições contadas no sino (ex.: "em andamento" → "concluída") só atualizava o total depois
// de um refresh manual, porque nada avisava o hook do sino que uma mutação tinha acontecido.
const NOTIFICATION_BADGE_RELEVANT_ROUTE_PATTERNS: RegExp[] = [
  /^\/admin\/demands(\/|$)/,
  /^\/admin\/clients\/[^/]+\/demands(\/|$)/,
  /^\/admin\/suggestions\/themes(\/|$)/,
  /^\/admin\/patrimony-inspections(\/|$)/,
];

const isNotificationBadgeMutation = (method: string | undefined, url: string | undefined): boolean => {
  if (!url || !method || !['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())) return false;
  const path = url.split('?')[0] || '';
  return NOTIFICATION_BADGE_RELEVANT_ROUTE_PATTERNS.some((pattern) => pattern.test(path));
};

// Interceptor para logging de respostas e tratamento de erros de autenticação
api.interceptors.response.use(
  (response: AxiosResponse) => {
    captureCsrfToken(response.headers as Record<string, string>);
    if (isNotificationBadgeMutation(response.config.method, response.config.url)) {
      notifyNotificationBadgeShouldRefresh();
    }
    return response;
  },
  async (error: AxiosError) => {
    captureCsrfToken(error.response?.headers as Record<string, string>);
    // Verificar se é um erro de rede (backend não disponível, crash, etc.)
    const isNetworkError = error.code === 'ERR_NETWORK' ||
                          error.code === 'ERR_SOCKET_NOT_CONNECTED' ||
                          error.code === 'ERR_CONNECTION_RESET' ||
                          error.code === 'ECONNABORTED' ||
                          error.message?.includes('Network Error') ||
                          error.message?.includes('ECONNREFUSED') ||
                          error.message?.includes('ERR_EMPTY_RESPONSE') ||
                          error.message?.includes('ECONNRESET');

    // Alinhar com o middleware: usuário em /app com assinatura bloqueada não deve ver só "erro ao carregar" em cascata
    const paywallCode = (error.response?.data as { code?: string } | undefined)?.code;
    if (
      error.response?.status === 403 &&
      (paywallCode === 'PAYMENT_OVERDUE' || paywallCode === 'TRIAL_EXPIRED') &&
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/app')
    ) {
      window.location.assign(ROUTES.PAYMENT);
    }
    
    // Verificar se é um erro de autenticação (401)
    const isUnauthorized = error.response?.status === 401;
    const requestUrl = error.config?.url || '';
    const isRequestPublicRoute = isPublicRoute(requestUrl);
    const isNoRefresh = isNoRefreshRoute(requestUrl);
    
    // Não logar erros de rede ou 401 em rotas públicas - são esperados
    const isValidation400 = error.response?.status === 400 && requestUrl.includes('/public/demands') && (error.config?.method === 'post' || error.config?.method === 'POST');
    if (
      !isNetworkError &&
      !(isUnauthorized && isRequestPublicRoute) &&
      !(isUnauthorized && isNoRefresh) &&
      // Não logar 400 de validação em POST /public/demands (tratado no form/hook)
      !isValidation400
    ) {
      logger.error('Erro na resposta:', error.response?.data || error.message);
    }
    
    // Tratar erro de autenticação apenas em rotas protegidas onde faz sentido tentar refresh
    if (isUnauthorized && !isRequestPublicRoute && !isNoRefresh) {
      // Não fazer logout se for erro na rota de perfil (pode ser validação de senha incorreta)
      const isProfileRoute = error.config?.url === '/auth/profile';
      const isRefreshRoute = error.config?.url === '/auth/refresh';
      
      if (!isProfileRoute && !isRefreshRoute) {
        const config = error.config as ExtendedInternalAxiosRequestConfig;

        if (config && !config._retry) {
          config._retry = true; // Marcar para evitar loop infinito

          try {
            // Tentar renovar token usando cookies HttpOnly
            const refreshResponse = await api.post('/auth/refresh');

            if (refreshResponse.data.success && config) {
              return api.request(config);
            }
            // Resposta 2xx mas success:false — o servidor recusou o refresh de forma explícita,
            // segue para o redirecionamento abaixo.
          } catch (refreshError) {
            const refreshStatus = (refreshError as AxiosError)?.response?.status;
            const isDefiniteRejection = refreshStatus === 401 || refreshStatus === 403;
            if (!isDefiniteRejection) {
              // Erro de rede/timeout/5xx tentando renovar (backend momentaneamente fora do ar,
              // conexão instável, etc.) — NÃO significa que a sessão expirou, o cookie de
              // refresh pode continuar perfeitamente válido. Bug real reportado: qualquer erro
              // no app estava sendo tratado como sessão expirada e redirecionando pro login.
              // Não redireciona aqui; só deixa esta requisição específica falhar (com mensagem
              // amigável) — a próxima ação do usuário tenta de novo.
              logger.error('Falha ao renovar token (indisponibilidade momentânea) — mantendo sessão', refreshError);
              (error as AxiosError & { userMessage?: string }).userMessage = getFriendlyErrorMessage(error);
              return Promise.reject(error);
            }
            logger.error('Refresh token rejeitado pelo servidor, fazendo logout');
          }
        }

        // Só chega aqui quando o servidor rejeitou o refresh de vez (401/403) ou quando a
        // resposta veio 2xx com success:false — logout implícito pelo backend ao expirar cookies.
        const isAppPublicRoute =
          window.location.pathname === '/' ||
          window.location.pathname === '/home' ||
          window.location.pathname.startsWith('/public-demand');
        if (!isAppPublicRoute) {
          window.location.href = ROUTES.LOGIN;
        }
      }
    }

    (error as AxiosError & { userMessage?: string }).userMessage = getFriendlyErrorMessage(error);

    return Promise.reject(error);
  }
);

export const apiService = {
  // Demands
  getDemandsData: (params?: { currentMonthClosedOnly?: boolean }) =>
    api.get('/admin/demands-data', { params }),
  
  // Estatísticas
  getStats: () => api.get('/admin/stats'),
  getReportsStats: () => api.get('/admin/reports/stats'),
  // Fonte única de verdade dos números de Relatórios (KPIs/categoria/performance/SLA/backlog) —
  // web e mobile chamam a mesma rota, garantindo o mesmo resultado (ver ReportsCalculationsService
  // no backend; achado real 22/08/2026: as duas cópias client-side desse cálculo divergiam entre
  // si em múltiplas rodadas de correção).
  getReportsSummary: (data: {
    clientId: string;
    startDate?: string;
    endDate?: string;
    dateType?: 'createdAt' | 'completedAt';
    categories?: string[];
    priorities?: string[];
    qrCodeIdentifiers?: string[];
    assigneeIds?: string[];
    providerIds?: string[];
    comparePreviousPeriod?: boolean;
  }) => api.post('/admin/reports/summary', data),

  // Relatório "Qualidade dos Checklists" (aba nova em Relatórios, permissão própria
  // checklistCompliance) — 3 níveis: área → execuções da área → detalhe de uma execução.
  // categories/priorities/assigneeIds/providerIds/qrCodeIdentifiers: mesmos filtros do painel
  // "FILTROS" da página, enviados como string separada por vírgula (mesmo padrão já usado em
  // getSuggestionThemes) — o backend faz `.split(',')`. Achado real (05/09): antes desta rodada,
  // o relatório só respeitava clientId+período; os demais campos do painel ficavam "aplicados"
  // visualmente mas eram ignorados pelos dados.
  getChecklistComplianceByArea: (params: {
    clientId: string;
    startDate?: string;
    endDate?: string;
    comparePreviousPeriod?: boolean;
    categories?: string;
    priorities?: string;
    assigneeIds?: string;
    providerIds?: string;
    qrCodeIdentifiers?: string;
  }) => api.get('/admin/reports/checklist-compliance', { params }),
  getChecklistComplianceExecutions: (params: {
    recurringDemandId: string;
    startDate?: string;
    endDate?: string;
    categories?: string;
    priorities?: string;
    assigneeIds?: string;
    providerIds?: string;
  }) => api.get('/admin/reports/checklist-compliance/executions', { params }),
  // Fase 2 — execuções "avulsas" (checklist sem RecurringDemand vinculada), sem % de aderência.
  getChecklistComplianceAdhocExecutions: (params: {
    clientId: string;
    startDate?: string;
    endDate?: string;
    categories?: string;
    priorities?: string;
    assigneeIds?: string;
    providerIds?: string;
    qrCodeIdentifiers?: string;
  }) => api.get('/admin/reports/checklist-compliance/adhoc', { params }),
  getChecklistComplianceExecutionDetail: (demandId: string) =>
    api.get(`/admin/reports/checklist-compliance/executions/${demandId}/detail`),

  // Central de Notificações (sino, pedido do usuário 05/09) — ver NotificationBadgeService.ts.
  // `clientIds`: o MESMO escopo de condomínio já selecionado na tela (DemandsContext.selectedClients)
  // — sem ele, o sino contava sempre o portfólio inteiro permitido, enquanto cada clique filtrava
  // só o condomínio selecionado (números que nunca batiam com mais de 1 condomínio no portfólio e
  // "Todos" não selecionado). Omitido/vazio mantém o comportamento anterior (todo o portfólio).
  getNotificationBadgeCounts: (clientIds?: string[]) =>
    api.get('/admin/notifications/badge-counts', clientIds?.length ? { params: { clientIds: clientIds.join(',') } } : undefined),
  // Pedido do usuário (10/09): indicador de pendência por condomínio no seletor (bolinha no botão
  // + detalhamento por condomínio ao abrir) — sempre o portfólio inteiro, nunca aceita `clientIds`
  // (ver getNotificationBadgeCountsByClient no backend).
  getNotificationBadgeCountsByClient: () => api.get('/admin/notifications/badge-counts-by-client'),
  getPendingChecklistDemandIds: () => api.get('/admin/notifications/pending-checklist-demand-ids'),

  // Análise de Tendências
  // Timeout generoso (backend agora tenta de novo uma vez em rate limit de IA, esperando o tempo
  // que o provedor pediu — até ~65s — antes de cair pro método básico; com vários grupos
  // sequenciais isso pode passar de 60s no total, então o timeout do front precisa cobrir isso).
  analyzeTrends: (data: { startDate?: string; endDate?: string; clientIds?: string[]; allowBasicFallback?: boolean }) =>
    api.post('/admin/reports/trends/analyze', data, { timeout: 180000 }),
  getTrendAnalyses: (params?: Record<string, unknown>) => api.get('/admin/reports/trends', { params }),
  addCorrectiveAction: (trendId: string, data: { action: string; expectedImpact: string; notes?: string }) =>
    api.post(`/admin/reports/trends/${trendId}/corrective-actions`, data),
  updateActionEffectiveness: (trendId: string, actionIndex: number, data: { effectiveness: string; notes?: string }) =>
    api.put(`/admin/reports/trends/${trendId}/corrective-actions/${actionIndex}`, data),
  
  // Clientes
  getClients: (params?: Record<string, unknown>) => api.get('/admin/clients', { params }),
  getClientById: (id: string) => api.get(`/admin/clients/${id}`),
  getPublicClientById: (id: string) => api.get(`/public/clients/${id}`, {
    params: {
      _t: Date.now() // Timestamp para evitar cache (não precisa dos headers, o timestamp já força nova requisição)
    }
  }), // Rota pública
  createClient: (data: unknown) => api.post('/admin/clients', data),
  getClientLimitInfo: () => api.get('/admin/clients/limit-info'),
  upgradeClients: (data: { targetCondoCapacity: number }) => api.post('/admin/clients/upgrade', data),
  downgradeClients: (data: { targetCondoCapacity: number; clientIdsToDeactivate?: string[] }) => api.post('/admin/clients/downgrade', data),
  downgradeNucleusCapacity: (data: { targetNucleusCapacity: number; clientIdsToDeactivate?: string[] }) =>
    api.post('/admin/clients/downgrade-nucleus-capacity', data),
  updateClient: (id: string, data: unknown) => api.put(`/admin/clients/${id}`, data),
  deleteClient: (id: string) => api.delete(`/admin/clients/${id}`),
  activateClient: (id: string) => api.put(`/admin/clients/${id}/activate`),
  deactivateClient: (id: string) => api.put(`/admin/clients/${id}/deactivate`),
  generateQRCode: (id: string) => api.get(`/admin/clients/${id}/qrcode`),
  
  // Usuários
  getUsers: (params?: Record<string, unknown>) => api.get('/admin/users', { params }),
  // Lista mínima (id+nome+foto), gate por `demands` em vez de `users` — ver requirePermission na
  // rota (backend/src/index.ts) e UserService.listAssignableUsers. Usada pra popular o select de
  // "responsável" em demandas (única/recorrente), sem exigir a permissão de administrar usuários.
  getAssignableUsers: () => api.get('/admin/users/assignable'),
  getUserById: (userId: string) => api.get(`/admin/users/${userId}`),
  createUser: (data: unknown) => api.post('/admin/users', data),
  updateUser: (userId: string, data: unknown) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId: string, demandActions?: { unassign?: boolean; reassignToUserId?: string }) => 
    api.delete(`/admin/users/${userId}`, { data: demandActions ? { demandActions } : undefined }),
  toggleUserStatus: (userId: string, demandActions?: { unassign?: boolean; reassignToUserId?: string }) => 
    api.patch(`/admin/users/${userId}/toggle-status`, demandActions ? { demandActions } : {}),
  getUserAssignedDemands: (userId: string) => api.get(`/admin/users/${userId}/assigned-demands`),
  
  // Demandas
  updateDemandStatus: (demandId: string, data: unknown) => api.put(`/admin/demands/${demandId}/status`, data),
  updateDemandPriority: (demandId: string, priority: string) => api.put(`/admin/demands/${demandId}/priority`, { priority }),
  updateDemandAssignee: (demandId: string, assigneeId: string | null) => api.put(`/admin/demands/${demandId}/assignee`, { assigneeId }),
  updateDemand: (demandId: string, data: unknown) => api.put(`/admin/demands/${demandId}`, data),
  createDemand: (data: unknown) => api.post('/admin/demands', data),
  // Endpoint público para formulário via QR code. Timeout maior que o padrão (10s) porque o
  // backend agora roda moderação anti-spam via Gemini (até 8s) em série com a detecção de
  // duplicatas antes de responder — no default, isso estourava o timeout do axios com alguma
  // frequência mesmo em submissões legítimas.
  createPublicDemand: (data: unknown) => api.post('/public/demands', data, { timeout: 20000 }),
  // Pedido do usuário (09/09): quando createPublicDemand responde com `alreadyBeingResolved`
  // (mesmo item de QR code + similaridade >= 90%), o solicitante pode acompanhar a demanda já
  // existente em vez de criar uma duplicata — o email informado é concatenado à lista de
  // notificação dessa demanda (mesmo mecanismo de `watchers` usado na unificação de demandas).
  followPublicDemand: (demandId: string, data: { clientId: string; email: string; name?: string | undefined }) =>
    api.post(`/public/demands/${demandId}/follow`, data),
  // timeout maior que o default: processa até 50 (modo preciso) ou 300 (modo leve) demandas de
  // uma vez, cada uma podendo escalar algumas comparações pra IA — mesmo bem mais rápido depois da
  // otimização de 21/08/2026 (heurística primeiro, IA só pra ambíguos, em paralelo), um lote grande
  // ainda pode passar do timeout default de 10s em cenários de rede mais lenta.
  getSimilarDemands: (demandId: string) => api.get(`/admin/demands/${demandId}/similar`, { timeout: 20000 }),
  getSimilarDemandsBatch: (demandIds: string[], lightweight?: boolean) =>
    api.post(
      '/admin/demands/similar-batch',
      { demandIds, ...(lightweight ? { lightweight: true } : {}) },
      { timeout: 30000 }
    ),
  cancelDemand: (demandId: string, cancellationReason: string) => api.put(`/admin/demands/${demandId}/cancel`, { cancellationReason }),
  addDemandImage: (demandId: string, imageBase64: string) => api.post(`/admin/demands/${demandId}/images`, { imageBase64 }),
  removeDemandImage: (demandId: string, imageBase64: string) =>
    api.delete(`/admin/demands/${demandId}/images`, { data: { imageBase64 } }),
  addDemandObservation: (demandId: string, text: string, operatorName?: string) =>
    api.post(`/admin/demands/${demandId}/observations`, { text, operatorName }),
  updateDemandObservation: (demandId: string, observationId: string, text: string) =>
    api.put(`/admin/demands/${demandId}/observations/${observationId}`, { text }),
  deleteDemandObservation: (demandId: string, observationId: string) =>
    api.delete(`/admin/demands/${demandId}/observations/${observationId}`),
  getMergedDemands: (demandId: string) => api.get(`/admin/demands/${demandId}/merged`),
  getDemandById: (demandId: string) => api.get(`/admin/demands/${demandId}`),
  mergeSimilarDemands: (mainDemandId: string, demandIdsToMerge: string[]) => api.post(`/admin/demands/${mainDemandId}/merge`, { demandIdsToMerge }),
  deleteDemand: (demandId: string, deletionReason: string) => api.delete(`/admin/demands/${demandId}`, { data: { deletionReason } }),
  deleteBulkDemands: (demandIds: string[], deletionReason: string) => api.post('/admin/demands/bulk-delete', { demandIds, deletionReason }),
  deleteAllClientDemands: (clientId: string, deletionReason: string) => api.delete(`/admin/clients/${clientId}/demands`, { data: { deletionReason } }),
  
  // Autenticação
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  verify2FA: (email: string, code: string) => api.post('/auth/verify-2fa', { email, code }),
  signup: (data: unknown) => api.post('/auth/signup', data, { timeout: CONFIG.API_SIGNUP_TIMEOUT }),
  requestSignupEmailCode: (email: string) => api.post('/auth/signup/request-code', { email }),
  verifySignupEmailCode: (signupVerificationId: string, code: string) => api.post('/auth/signup/verify-code', { signupVerificationId, code }),
  checkCNPJ: (cnpj: string) => api.get('/auth/check-cnpj', { params: { cnpj } }),
  refreshToken: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  verifyToken: () => api.get('/auth/verify'),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => api.post('/auth/reset-password', { token, password }),
    getProfile: () => api.get('/auth/profile'),
    getTenantInfo: () => api.get('/auth/tenant'),
    updateProfile: (data: unknown) => api.put('/auth/profile', data),
    updateTenantLogo: (logo: string | null) => api.put('/auth/tenant/logo', { logo }),
  
  // Termos
  getRequiredTerms: () => api.get('/terms/required'),
  getActiveTermByType: (type: 'terms_of_use' | 'lgpd' | 'resale_prohibition' | 'other', language: string = 'pt-BR') =>
    api.get(`/terms/type/${encodeURIComponent(type)}`, { params: { language } }),

  // Onboarding (primeiro acesso guiado)
  getOnboardingStatus: () => api.get('/onboarding/status'),
  completeOnboarding: () => api.post('/onboarding/complete'),
  markOnboardingQrExported: () => api.post('/onboarding/qr-exported'),

  // Account deletion
  requestAccountDeletion: () => api.post('/account-deletion/request'),
  confirmAccountDeletionCode: (code: string) => api.post('/account-deletion/confirm-code', { code }),
  startAccountDeletion: (phrase: string) => api.post('/account-deletion/start', { phrase }),
  getAccountDeletionStatus: () => api.get('/account-deletion/status'),
  requestAccountDeletionPublic: (email: string, password: string) => api.post('/account-deletion/public/request', { email, password }),
  confirmAccountDeletionCodePublic: (email: string, code: string) => api.post('/account-deletion/public/confirm-code', { email, code }),
  startAccountDeletionPublic: (email: string, phrase: string) => api.post('/account-deletion/public/start', { email, phrase }),
  getAccountDeletionStatusPublic: (email: string, requestId: string) => api.post('/account-deletion/public/status', { email, requestId }),
  
  // Admin - Termos (requer autenticação)
  getAllTerms: (params?: Record<string, unknown>) => api.get('/admin/terms', { params }),
  getTermById: (id: string) => api.get(`/admin/terms/${id}`),
  createTerm: (data: unknown) => api.post('/admin/terms', data),
  updateTermMetadata: (id: string, data: unknown) => api.put(`/admin/terms/${id}`, data),
  deleteTerm: (id: string) => api.delete(`/admin/terms/${id}`),
  createTermVersion: (id: string, data: unknown) => api.post(`/admin/terms/${id}/version`, data),
  getTermVersions: (id: string) => api.get(`/admin/terms/${id}/versions`),
  getTermsAudit: (params?: Record<string, unknown>) => api.get('/admin/terms/audit', { params }),
  
  // Precificação
  getPricingConfig: () => api.get('/pricing/config'),
  /** Dias de trial padrão (`trial_period_config.defaultTrialPeriodDays`) para landing */
  getPublicTrialPeriodDefault: () => api.get('/public/trial-period-default'),
  /** E-mail, WhatsApp e horário — apenas `contact` da support_config (control), endpoint público */
  getPublicSupportContact: () => api.get('/public/support-contact'),
  getTenantBilling: () => api.get('/pricing/billing'),
  updateMyBilling: (data: { packageType?: string; contractedExtraUsers?: number; contractedClientCapacity?: number; contractedNucleusCapacity?: number; paymentDay?: number | null }) =>
    api.put('/pricing/billing', data),
  updatePricingConfig: (data: unknown) => api.put('/pricing/config', data),
  
  // User Limits
  getUserLimitInfo: () => api.get('/admin/users/limit-info'),
  checkUserRemoval: (userId: string) => api.get(`/admin/users/${userId}/check-removal`),
  downgradeUsers: (data: { targetUserCount: number; userIdsToDeactivate: string[] }) => 
    api.post('/admin/users/downgrade', data),
  upgradeUsers: (data: { targetExtraUsersCount: number }) => 
    api.post('/admin/users/upgrade', data),
  initPricingConfig: () => api.post('/pricing/init'),
  
  // Roles
  getRoles: (params?: Record<string, unknown>) => api.get('/admin/roles', { params }),
  getRoleById: (roleId: string) => api.get(`/admin/roles/${roleId}`),
  createRole: (data: unknown) => api.post('/admin/roles', data),
  updateRole: (roleId: string, data: unknown) => api.put(`/admin/roles/${roleId}`, data),
  deleteRole: (roleId: string, targetRoleId?: string) => 
    api.delete(`/admin/roles/${roleId}`, { data: targetRoleId ? { targetRoleId } : undefined }),
  getRoleUsers: (roleId: string) => api.get(`/admin/roles/${roleId}/users`),
  toggleRoleStatus: (roleId: string) => api.patch(`/admin/roles/${roleId}/toggle-status`),
  
  // Configurações
  getAllConfig: () => api.get('/config/all'),
  getGeneralConfig: () => api.get('/config/general'),
  updateGeneralConfig: (data: unknown) => api.put('/config/general', data),
  validateAiKey: (data: { apiKey: string; provider: 'openai' | 'gemini' }) => api.post('/config/validate-ai-key', data),
  getDemandsConfig: () => api.get('/config/demand-management'),
  updateDemandColumns: (data: unknown) => api.put('/config/demand-management/columns', data),
  updateDemandPriorities: (data: unknown) => api.put('/config/demand-management/priorities', data),
  updateDemandCategories: (data: unknown) => api.put('/config/demand-management/categories', data),
  updateDemandsConfig: (data: unknown) => api.put('/config/demand-management', data),
  getReportsConfig: () => api.get('/config/reports'),
  updateReportsConfig: (data: unknown) => api.put('/config/reports', data),
  getEmailConfig: () => api.get('/config/email'),
  updateEmailConfig: (data: unknown) => api.put('/config/email', data),
  testEmailConfig: (testEmail?: string, attachment?: File) => {
    if (attachment) {
      const formData = new FormData();
      if (testEmail) formData.append('testEmail', testEmail);
      formData.append('attachment', attachment);
      return api.post('/config/email/test', formData, {
        headers: { 'Content-Type': undefined } as unknown as Record<string, string>
      });
    }
    const body = testEmail ? { testEmail } : {};
    return api.post('/config/email/test', body);
  },
  getCronJobStatus: () => api.get('/config/cron-job/status'),
  testCronJob: () => api.post('/config/cron-job/test'),
  getWhatsappCloudBinding: () => api.get('/config/whatsapp-cloud/binding'),
  putWhatsappCloudBinding: (data: Record<string, unknown>) => api.put('/config/whatsapp-cloud/binding', data),
  deleteWhatsappCloudBinding: () => api.delete('/config/whatsapp-cloud/binding'),
  getWhatsappCloudSettings: () => api.get('/config/whatsapp-cloud/settings'),
  putWhatsappCloudSettings: (data: Record<string, unknown>) =>
    api.put('/config/whatsapp-cloud/settings', data),
  getWhatsappCloudMessages: () => api.get('/config/whatsapp-cloud/messages'),
  postWhatsappCloudTestMessage: (body: { to: string }) =>
    api.post('/config/whatsapp-cloud/test-message', body),
  adminGetWhatsappBinding: (userId: string) =>
    api.get(`/config/whatsapp-cloud/users/${userId}/binding`),
  adminPutWhatsappBinding: (userId: string, data: Record<string, unknown>) =>
    api.put(`/config/whatsapp-cloud/users/${userId}/binding`, data),
  adminDeleteWhatsappBinding: (userId: string) =>
    api.delete(`/config/whatsapp-cloud/users/${userId}/binding`),

  // Configurações públicas (sem autenticação)
  getPublicDemandConfigByClientId: (clientId: string) => api.get(`/public/config/demand-management/${clientId}`),
  getPublicStats: (noCache?: boolean) => {
    const params = noCache ? { _t: Date.now() } : {};
    return api.get('/public/stats', { params });
  },
  getPublicTenants: (forceUpdate = false) => {
    const params = forceUpdate ? { _t: Date.now() } : {};
    return api.get('/public/tenants', { params });
  },
  
  // Demandas Recorrentes
  getRecurringDemands: (clientId: string) => api.get(`/recurring-demands/client/${clientId}`),
  getRecurringDemand: (id: string) => api.get(`/recurring-demands/${id}`),
  createRecurringDemand: (data: unknown) => api.post('/recurring-demands', data),
  updateRecurringDemand: (id: string, data: unknown, updateOption?: 'all' | 'future' | 'past') => {
    const params = updateOption ? { updateOption } : {};
    return api.put(`/recurring-demands/${id}`, data, { params });
  },
  deleteRecurringDemand: (id: string, deleteOption?: 'all' | 'future' | 'past') => {
    const params = deleteOption ? { deleteOption } : {};
    return api.delete(`/recurring-demands/${id}`, { params });
  },
  
  // QR Codes
  getQRCodes: (clientId: string) => api.get(`/qr-codes/client/${clientId}`),
  getQRCode: (id: string) => api.get(`/qr-codes/${id}`),
  createQRCode: (data: unknown) => api.post('/qr-codes', data),
  updateQRCode: (id: string, data: unknown) => api.put(`/qr-codes/${id}`, data),
  deleteQRCode: (id: string) => api.delete(`/qr-codes/${id}`),
  getPublicQRCodeByToken: (token: string) => api.get(`/public/qr-codes/token/${token}`, {
    params: {
      _t: Date.now() // Timestamp para evitar cache
    }
  }), // Rota pública segura usando token (recomendada)

  // Caixa de sugestões dos moradores
  getSuggestionBoxConfig: (clientId: string) => api.get(`/admin/suggestions/box/${clientId}`),
  upsertSuggestionBoxConfig: (clientId: string, data: { isActive: boolean }) =>
    api.put(`/admin/suggestions/box/${clientId}`, data),

  // Painel Público da Operação (Relatório B)
  setClientPublicReportEnabled: (clientId: string, enabled: boolean) =>
    api.put(`/admin/clients/${clientId}/public-report`, { enabled }),
  // Mensagem de conscientização do painel público (pedido do usuário, 10/09) — editável
  // independente de ligar/desligar.
  updateClientPublicReportMessage: (clientId: string, message: string) =>
    api.put(`/admin/clients/${clientId}/public-report-message`, { message }),
  // Aderência ao painel público (acessos/dia, últimos 30 dias) — ver ClientPublicReportEngagement.tsx.
  getClientPublicReportEngagement: (clientId: string) =>
    api.get(`/admin/clients/${clientId}/public-report/engagement`),
  // Sem `_t` de cache-busting (ao contrário dos GETs públicos acima) — este endpoint é cacheado no
  // backend por 5 min (httpCache), de propósito: resumo agregado, não precisa ser instantâneo.
  getPublicOperationReport: (token: string, days?: 30 | 90 | 365) =>
    api.get(`/public/reports/${encodeURIComponent(token)}`, { params: days ? { days } : {} }),
  getSuggestionThemes: (clientIds: string[]) => api.get('/admin/suggestions/themes', { params: { clientIds: clientIds.join(',') } }),
  getSuggestionThemeDetail: (id: string) => api.get(`/admin/suggestions/themes/${id}`),
  updateSuggestionTheme: (id: string, data: { status?: string; managementNotes?: string }) =>
    api.patch(`/admin/suggestions/themes/${id}`, data),
  // Mesmo motivo do timeout de analyzeTrends acima — retry de rate limit de IA pode passar de 60s.
  triggerSuggestionAnalysis: (clientIds: string[], allowBasicFallback?: boolean) =>
    api.post('/admin/suggestions/analyze', { clientIds, ...(allowBasicFallback ? { allowBasicFallback } : {}) }, { timeout: 180000 }),
  promoteSuggestionTheme: (id: string, data?: { category?: string }) =>
    api.post(`/admin/suggestions/themes/${id}/promote`, data || {}),
  getPublicSuggestionBox: (token: string) =>
    api.get(`/public/suggestions/box/${encodeURIComponent(token)}`, { params: { _t: Date.now() } }),
  getPublicSuggestionThemes: (token: string) =>
    api.get(`/public/suggestions/box/${encodeURIComponent(token)}/themes`, { params: { _t: Date.now() } }),
  getPublicSuggestionThemeSuggestions: (token: string, themeId: string) =>
    api.get(
      `/public/suggestions/box/${encodeURIComponent(token)}/themes/${encodeURIComponent(themeId)}/suggestions`,
      { params: { _t: Date.now() } }
    ),
  createPublicSuggestion: (data: { token: string; content: string; residentName: string; block?: string; unitNumber?: string; themeId?: string }) =>
    api.post('/public/suggestions', data),

  // Prestadores de serviço
  getProviders: () => api.get('/admin/providers', { params: { _t: Date.now() } }),
  getProvider: (id: string) => api.get(`/admin/providers/${id}`),
  createProvider: (data: unknown) => api.post('/admin/providers', data),
  updateProvider: (id: string, data: unknown) => api.put(`/admin/providers/${id}`, data),
  regenerateProviderCode: (id: string) => api.post(`/admin/providers/${id}/regenerate-code`),
  deleteProvider: (id: string, demandActions?: { unassign?: boolean; reassignToProviderId?: string }) =>
    api.delete(`/admin/providers/${id}`, { data: demandActions ? { demandActions } : undefined }),
  toggleProviderStatus: (id: string, demandActions?: { unassign?: boolean; reassignToProviderId?: string }) =>
    api.patch(`/admin/providers/${id}/toggle-status`, demandActions ? { demandActions } : {}),
  getProviderAssignedDemands: (id: string) => api.get(`/admin/providers/${id}/assigned-demands`),
  sendPendingWhatsapp: (providerId: string) => api.post(`/admin/providers/${providerId}/send-pending-whatsapp`),
  sendPendingEmail: (providerId: string) => api.post(`/admin/providers/${providerId}/send-pending-email`),
  forwardDemandToProvider: (demandId: string, data: { providerId: string; channel: 'email' | 'whatsapp'; targetStatus?: string }) =>
    api.put(`/admin/demands/${demandId}/forward-provider`, data),
  bulkForwardDemandsToProvider: (data: { demandIds: string[]; providerId: string; channel: 'email' | 'whatsapp' }) =>
    api.post('/admin/demands/bulk-forward-provider', data),
  verifyProviderCode: (data: { qrToken: string; accessCode: string }) =>
    api.post('/public/providers/verify', data),
  completeDemandsAsProvider: (data: { qrToken: string; accessCode: string; demandIds: string[]; executorName: string }) =>
    api.post('/public/providers/complete', data),
  verifyCompletionLink: (data: { token: string }) =>
    api.post('/public/providers/completion-link/verify', data),
  completeByCompletionLink: (data: { token: string; demandIds: string[]; executorName: string }) =>
    api.post('/public/providers/completion-link/complete', data),

  // Checklists (cadastro administrativo reutilizável) — pedido do usuário (02/09): passa a
  // pertencer a um condomínio, clientId obrigatório na listagem/criação.
  getChecklists: (clientId: string) => api.get('/admin/checklists', { params: { clientId, _t: Date.now() } }),
  getChecklist: (id: string) => api.get(`/admin/checklists/${id}`),
  createChecklist: (data: unknown) => api.post('/admin/checklists', data),
  updateChecklist: (id: string, data: unknown) => api.put(`/admin/checklists/${id}`, data),
  toggleChecklistStatus: (id: string) => api.patch(`/admin/checklists/${id}/toggle-status`),
  deleteChecklist: (id: string) => api.delete(`/admin/checklists/${id}`),
  cloneChecklists: (sourceIds: string[], targetClientId: string) =>
    api.post('/admin/checklists/clone', { sourceIds, targetClientId }),

  // Listas de patrimônio (Fase 4, cadastro administrativo reutilizável) — mesma regra de clientId
  // obrigatório.
  getPatrimonyLists: (clientId: string) => api.get('/admin/patrimony-lists', { params: { clientId, _t: Date.now() } }),
  getPatrimonyList: (id: string) => api.get(`/admin/patrimony-lists/${id}`),
  createPatrimonyList: (data: unknown) => api.post('/admin/patrimony-lists', data),
  updatePatrimonyList: (id: string, data: unknown) => api.put(`/admin/patrimony-lists/${id}`, data),
  togglePatrimonyListStatus: (id: string) => api.patch(`/admin/patrimony-lists/${id}/toggle-status`),
  deletePatrimonyList: (id: string) => api.delete(`/admin/patrimony-lists/${id}`),
  clonePatrimonyLists: (sourceIds: string[], targetClientId: string) =>
    api.post('/admin/patrimony-lists/clone', { sourceIds, targetClientId }),
  // Pedido do usuário (03/09): fazer a conferência direto da tela de Patrimônio, sem QR code.
  // Pedido do usuário (08/09): não cria mais uma Demand — só a inspeção avulsa, pronta pra tela de
  // conferência (ver getPatrimonyInspectionById).
  startPatrimonyConference: (listId: string) => api.post(`/admin/patrimony-lists/${listId}/start-conference`),
  getPatrimonyInspectionById: (inspectionId: string) => api.get(`/admin/patrimony-inspections/${inspectionId}`),

  // Histórico de execução/inspeção (Fase 5 — visão do gestor em DemandDetails.tsx). Sempre
  // createIfMissing=false: o gestor só consulta o que já existe, nunca inicia uma execução.
  getDemandChecklistExecution: (demandId: string) =>
    api.get(`/admin/demands/${demandId}/checklist-execution`, { params: { createIfMissing: false } }),
  listChecklistTaskEvidence: (executionId: string, taskId: string) =>
    api.get(`/admin/checklist-executions/${executionId}/tasks/${taskId}/evidence`),
  getDemandPatrimonyInspection: (demandId: string) =>
    api.get(`/admin/demands/${demandId}/patrimony-inspection`, { params: { createIfMissing: false } }),
  listPatrimonyItemEvidence: (inspectionId: string, itemId: string) =>
    api.get(`/admin/patrimony-inspections/${inspectionId}/items/${itemId}/evidence`),
  // Pedido do usuário (08/09): anexar evidência (até 2 fotos, opcional por padrão, configurável
  // no cadastro) também na conferência pela tela web — mesmos endpoints já usados pelo app mobile.
  addPatrimonyItemEvidence: (inspectionId: string, itemId: string, imageBase64: string) =>
    api.post(`/admin/patrimony-inspections/${inspectionId}/items/${itemId}/evidence`, { imageBase64 }),
  deletePatrimonyItemEvidence: (inspectionId: string, itemId: string, evidenceId: string) =>
    api.delete(`/admin/patrimony-inspections/${inspectionId}/items/${itemId}/evidence/${evidenceId}`),

  // Conferência de patrimônio pela tela web (pedido do usuário, 03/09) — mesmos endpoints já
  // usados pelo app mobile na operação via QR code, agora também consumidos aqui.
  getOrCreatePatrimonyInspection: (demandId: string) => api.get(`/admin/demands/${demandId}/patrimony-inspection`),
  inspectPatrimonyItem: (
    inspectionId: string,
    itemId: string,
    data: { foundQuantity: number; condition: string; observation?: string | undefined }
  ) => api.patch(`/admin/patrimony-inspections/${inspectionId}/items/${itemId}/inspect`, data),
  reportPatrimonyDamage: (inspectionId: string, itemId: string, data?: { observationText?: string | undefined }) =>
    api.post(`/admin/patrimony-inspections/${inspectionId}/items/${itemId}/report-damage`, data || {}),

  // Impersonação
  startImpersonation: (data: { targetUserId: string; targetTenantId?: string; reason?: string }) => 
    api.post('/impersonation/start', data),
  stopImpersonation: () => api.post('/impersonation/stop'),
  getImpersonationStatus: () => api.get('/impersonation/status'),
  request2FA: (data: { targetUserId: string; targetTenantId: string; reason: string }) =>
    api.post('/impersonation/request-2fa', data),
  
  // Trial Period
  getTrialPeriodInfo: () => api.get('/trial-period/info'),
  getTrialPeriodDays: () => api.get('/trial-period/days'),
  getPaymentInfo: () => api.get('/trial-period/payment-info'),
  activateSubscription: () => api.post('/trial-period/activate-subscription'),
  cancelPendingClientCapacityUpgrade: () => api.post('/pricing/cancel-client-capacity-upgrade'),
  
  // Payment
  // Banco Inter pode demorar para criar cobrança (evita cancelamento em 10s)
  generateBoleto: () => api.post('/payment/generate-boleto', undefined, { timeout: 60000 }),
  generatePix: () => api.post('/payment/generate-pix', undefined, { timeout: 60000 }),
  confirmPayment: (paymentRequestId: string) =>
    api.post('/payment/confirm-payment', { paymentRequestId }, { timeout: 60000 }),
  getPayments: () => api.get('/payment/list'),
  getPendingPayment: () => api.get('/payment/pending'),
  getBoletoPdf: (paymentId: string) =>
    api.get(`/payment/boleto/${encodeURIComponent(paymentId)}/pdf`, {
      responseType: 'blob',
      timeout: 60000,
    }),
  getPaymentStatus: (paymentRequestId: string) =>
    api.get<{ success: boolean; data?: Record<string, unknown> }>(
      `/payment/status/${encodeURIComponent(paymentRequestId)}`
    ),

  // Billing (faturas, pagamentos, NF-e - proxy para vover-billing)
  billing: {
    listInvoices: (params?: { status?: string; limit?: number; offset?: number }) =>
      api.get('/billing/invoices', { params }),
    getInvoice: (invoiceId: string) => api.get(`/billing/invoices/${invoiceId}`),
    /** Fatura em aberto (mesma base do GET /payment/pending com billing) */
    getPendingInvoice: () => api.get('/billing/pending-invoice'),
    getSubscriptionPayContext: () =>
      api.get<{
        success: boolean;
        data?: {
          billingPeriod: string;
          dueDate: string;
          daysUntilDue: number;
          opensDays: number;
          inPaymentWindow: boolean;
          periodPaid: boolean;
          estimatedAmount: number;
          currency: string;
          pendingInvoiceExternalId: string | null;
        };
      }>('/billing/subscription-pay-context'),
    /** Competência atual: cria fatura no billing na primeira cobrança (sem cron). */
    subscriptionGeneratePayment: (billingType: 'BOLETO' | 'PIX') =>
      api.post(`/billing/subscription/generate-payment`, { billingType }, { timeout: 120_000 }),
    generatePayment: (invoiceId: string, billingType: 'BOLETO' | 'PIX') =>
      api.post(`/billing/invoices/${invoiceId}/generate-payment`, { billingType }),
    listPayments: (params?: { limit?: number; offset?: number }) =>
      api.get('/billing/payments', { params }),
    listNfse: (params?: { limit?: number; offset?: number }) =>
      api.get('/billing/nfse', { params }),
    getHints: () =>
      api.get<{
        success: boolean;
        data?: {
          dueReminderDays?: number[];
          overdueReminderDaysAfter?: number[];
          interPixExpirationSeconds?: number | null;
          nextPeriodPaymentOpensDays?: number;
        };
      }>('/billing/hints'),
  },

  getBillingGlobalConfig: () =>
    api.get<{ success: boolean; data?: Record<string, unknown> }>('/billing/config'),
  updateBillingGlobalConfig: (data: Record<string, unknown>) =>
    api.put<{ success: boolean; data?: Record<string, unknown> }>('/billing/config', data),
  verify2FAAndStart: (data: { targetUserId: string; targetTenantId: string; reason: string; code: string }) =>
    api.post('/impersonation/start', { ...data, twoFACode: data.code }),
  getImpersonationAccessReport: () => api.get('/impersonation/access-report'),
  
  // System Admin (apenas para system admins)
  getSystemAdminTenants: () => api.get('/system-admin/tenants'),
  getSystemAdminTenantUsers: (tenantId: string) => api.get(`/system-admin/tenants/${tenantId}/users`),
  getSystemAdminPlatformAuditLogs: (params?: Record<string, string>) =>
    api.get<{
      success: boolean;
      data?: { items: unknown[]; nextCursor?: string; appliedRange?: { from: string; to: string } };
    }>('/system-admin/audit/platform-logs', { params }),
  getSystemAdminBillingNotificationAudits: (params?: Record<string, string>) =>
    api.get<{
      success: boolean;
      data?: { items: unknown[]; nextCursor?: string; appliedRange?: { from: string; to: string } };
    }>('/system-admin/audit/billing-notifications', { params }),
  
  // Support Config
  getSupportConfig: () => api.get('/support/config'),
  updateSupportConfig: (data: unknown) => api.put('/support/config', data),

  // Centro de Recursos (vídeos no R2)
  getResourceVideos: () => api.get<{ data: { tutoriais: string[]; divulgacao: string[] } }>('/resources/videos'),
  getPublicVideos: () =>
    api.get<{ data: { url: string; filename: string; mediaType?: 'video' | 'image' }[] }>(
      '/resources/videos/public'
    ),
  getSignedVideoUrl: (category: 'tutorials' | 'downloads', filename: string, download = false) =>
    api.get<{ data: { url: string } }>(
      `/resources/videos/signed/${category}/${encodeURIComponent(filename)}${download ? '?download=true' : ''}`
    ),
};

export default api;
